import React, { useEffect, useState } from "react";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { authPut, authDelete } from "../../../lib";

const useStyles = makeStyles((theme) => ({
  fields: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1.5),
    paddingTop: theme.spacing(0.5),
    minWidth: 460,
  },
  /* MUI draws a circular focus/ripple backdrop behind a checkbox once it is
     clicked, which reads as a heavy blue box around the tick. Nothing here
     needs it — the tick itself shows the state. */
  flags: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    "& .MuiIconButton-root": { backgroundColor: "transparent !important" },
    "& .MuiIconButton-root:focus": { outline: "none" },
    "& .MuiTouchRipple-root": { display: "none" },
  },
  title: { fontSize: 13, color: theme.palette.text.secondary, width: "100%" },
  error: { color: "#c62828", fontSize: 13, marginTop: theme.spacing(1) },
  warn: { color: "#8a6100", fontSize: 12.5, marginTop: theme.spacing(1) },
  spacer: { flex: 1 },
  remove: {
    color: "#c62828",
    border: "1px solid rgba(198,40,40,0.4)",
    backgroundColor: "rgba(198,40,40,0.04)",
    "&:hover": { backgroundColor: "rgba(198,40,40,0.1)", borderColor: "#c62828" },
  },
}));

const FLAGS = [
  ["price_on_special", "Item on special"],
  ["special_order", "Special order"],
  ["papers_included", "Papers included"],
];

/**
 * Edits one line item, or removes it.
 *
 * Changing the model number swaps the item purchased: the server resolves the
 * new model against the catalog and carries its id and title over. A model it
 * cannot find is still saved — legacy orders reference models the catalog has
 * since dropped — but the dialog says so rather than pretending it matched.
 */
export function OrderItemDialog({ orderId, item, onClose, onSaved }) {
  const classes = useStyles();

  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [warn, setWarn] = useState("");

  useEffect(() => {
    if (!item) return;
    setForm({
      model_no: item.model_no ?? "",
      qty: item.qty ?? 1,
      price: item.price ?? 0,
      price_on_special: !!Number(item.price_on_special),
      special_order: !!Number(item.special_order),
      papers_included: !!Number(item.papers_included),
    });
    setError("");
    setWarn("");
  }, [item]);

  if (!item) return null;

  const set = (name, isFlag) => (e) =>
    setForm((s) => ({ ...s, [name]: isFlag ? e.target.checked : e.target.value }));

  const save = async () => {
    const body = {};
    Object.entries(form).forEach(([k, v]) => {
      const before = FLAGS.some(([f]) => f === k) ? !!Number(item[k]) : item[k] ?? "";
      if (String(before) === String(v)) return;
      body[k] = k === "qty" || k === "price" ? Number(v) : v;
    });

    if (!Object.keys(body).length) {
      onClose();
      return;
    }

    setBusy(true);
    const { data, error: err } = await authPut(
      `/orders/${orderId}/items/${item.order_item_id}`,
      body,
    );
    setBusy(false);

    if (err) {
      setError(err.message || "Could not save.");
      return;
    }
    // matched === false means the model saved but nothing in the catalog
    // carries it, so item_id and title still point at the previous watch.
    if (data?.matched === false) {
      setWarn(
        "Saved, but no catalog item has that model number — the description and " +
          "item link still point at the previous watch.",
      );
      onSaved({ keepOpen: true });
      return;
    }
    onSaved();
  };

  const remove = async () => {
    if (!window.confirm(`Remove ${item.model_no} from this order?`)) return;
    setBusy(true);
    const { error: err } = await authDelete(`/orders/${orderId}/items/${item.order_item_id}`);
    setBusy(false);
    if (err) {
      setError(err.message || "Could not remove.");
      return;
    }
    onSaved();
  };

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="sm">
      <DialogTitle>Edit Item</DialogTitle>
      <DialogContent>
        <div className={classes.fields}>
          <Typography className={classes.title}>{item.title}</Typography>

          <TextField
            label="Model number"
            style={{ flex: "1 1 240px" }}
            helperText="Changing this swaps the item purchased"
            value={form.model_no ?? ""}
            onChange={set("model_no")}
          />
          <TextField
            label="Qty"
            type="number"
            style={{ flex: "0 0 90px" }}
            value={form.qty ?? 1}
            onChange={set("qty")}
          />
          <TextField
            label="Price each"
            type="number"
            style={{ flex: "0 0 140px" }}
            value={form.price ?? 0}
            onChange={set("price")}
          />

          <div className={classes.flags}>
            {FLAGS.map(([name, label]) => (
              <FormControlLabel
                key={name}
                control={
                  <Checkbox
                    color="primary"
                    checked={!!form[name]}
                    onChange={set(name, true)}
                  />
                }
                label={label}
              />
            ))}
          </div>
        </div>

        {warn && <div className={classes.warn}>{warn}</div>}
        {error && <div className={classes.error}>{error}</div>}
      </DialogContent>

      <DialogActions>
        <Button onClick={remove} disabled={busy} size="small" className={classes.remove}>
          Remove item
        </Button>
        <span className={classes.spacer} />
        <Button onClick={onClose} disabled={busy} size="small">
          Close
        </Button>
        <Button onClick={save} disabled={busy} size="small" variant="contained" color="primary">
          {busy ? "Saving…" : "Save changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default OrderItemDialog;
